import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Box, TextField, Button, Paper, Autocomplete, ToggleButton, ToggleButtonGroup, Stack, Divider, FormControlLabel, Checkbox, Snackbar, Alert
} from '@mui/material';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

export default function EditCoin() {
    const navigate = useNavigate();
    const { coinId } = useParams();
    const [searchParams] = useSearchParams();
    const initialSpecimenId = searchParams.get('specimenId');

    const [coins, setCoins] = useState([]);
    const [selectedCoin, setSelectedCoin] = useState(null);
    const [editMode, setEditMode] = useState('type'); // 'type' or 'specimen'

    // Notification State
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };
    const handleCloseNotification = (event, reason) => {
        if (reason === 'clickaway') return;
        setNotification({ ...notification, open: false });
    };

    // Specimen selection
    const [selectedSpecimen, setSelectedSpecimen] = useState(null);

    // Form States
    const [coinData, setCoinData] = useState({});
    const [specimenData, setSpecimenData] = useState({});

    // 1. Fetch all coins for search (if no ID) or specific coin (if ID)
    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001')}/coins`)
            .then(res => {
                setCoins(res.data);
                if (coinId) {
                    const found = res.data.find(c => c.pcgs_id === parseInt(coinId) || c.id === parseInt(coinId));
                    if (found) {
                        setSelectedCoin(found);
                        initializeForm(found);
                    }
                }
            })
            .catch(err => console.error("Error loading coins:", err));
    }, [coinId]);

    // 2. Initialize form when coin is selected
    const initializeForm = (coin) => {
        // Coin Type Data
        setCoinData({
            pcgs_id: coin.pcgs_id,
            country: coin.country,
            denomination: coin.denomination,
            series_name: coin.series_name,
            series_date: coin.series_date,
            type: coin.type,
            date: coin.date,
            major: coin.major,
            minor: coin.minor,
            mint: coin.mint,
            status: coin.status
        });

        // Specimen Selection Logic
        if (initialSpecimenId && coin.specimens) {
            const spec = coin.specimens.find(s => s.id === parseInt(initialSpecimenId));
            if (spec) {
                setEditMode('specimen');
                setSelectedSpecimen(spec);
                setSpecimenData(spec);
            }
        } else if (coin.specimens && coin.specimens.length > 0) {
            // Default to first specimen if in specimen mode, or just wait user selection
            // For now, let's just create a blank slate or select first
        }
    };

    const handleCoinSelect = (e, newValue) => {
        setSelectedCoin(newValue);
        if (newValue) {
            initializeForm(newValue);
            // Update URL to match selected coin without reload if possible, or just stay on generic page
            // For simplicity, we just set state. 
        }
    };

    const handleModeChange = (e, newMode) => {
        if (newMode) setEditMode(newMode);
    };

    const handleSpecimenSelect = (e, newValue) => {
        setSelectedSpecimen(newValue);
        if (newValue) {
            setSpecimenData(newValue);
        }
    };

    const handleCoinChange = (e) => {
        const { name, value } = e.target;
        setCoinData(prev => ({ ...prev, [name]: value }));
    };

    const handleSpecimenChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSpecimenData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!selectedCoin) return;

        let updatedCoin = { ...selectedCoin };

        if (editMode === 'type') {
            updatedCoin = { ...updatedCoin, ...coinData };
        } else {
            if (!selectedSpecimen) return;
            // Update the specific specimen in the array
            const originalSpecimens = selectedCoin.specimens || [];
            updatedCoin.specimens = originalSpecimens.map(s =>
                s.id === selectedSpecimen.id ? { ...specimenData, id: s.id } : s
            );
        }

        const updateId = selectedCoin.id || selectedCoin.pcgs_id;
        axios.put(`${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001')}/coins/${updateId}`, updatedCoin)
            .then(() => {
                showNotification('Coin updated successfully!', 'success');
                setTimeout(() => navigate('/'), 1500);
            })
            .catch(err => {
                console.error('Error updating coin:', err);
                showNotification('Failed to update coin. ' + (err.response?.data ? JSON.stringify(err.response.data) : err.message), 'error');
            });
    };


    const handleDeleteCoin = () => {
        if (!selectedCoin) return;
        if (!window.confirm(`Are you sure you want to delete the coin type "${selectedCoin.series_name} - ${selectedCoin.denomination}" and ALL its specimens? This cannot be undone.`)) {
            return;
        }

        const deleteId = selectedCoin.id || selectedCoin.pcgs_id;
        axios.delete(`${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001')}/coins/${deleteId}`)
            .then(() => {
                showNotification('Coin type deleted successfully!', 'success');
                setTimeout(() => navigate('/'), 1500);
            })
            .catch(err => {
                console.error('Error deleting coin:', err);
                showNotification('Failed to delete coin.', 'error');
            });
    };

    const handleDeleteSpecimen = () => {
        if (!selectedCoin || !selectedSpecimen) return;
        if (!window.confirm(`Are you sure you want to delete this specimen (Grade: ${selectedSpecimen.grade})?`)) {
            return;
        }

        const updatedSpecimens = selectedCoin.specimens.filter(s => s.id !== selectedSpecimen.id);
        const updatedCoin = { ...selectedCoin, specimens: updatedSpecimens };
        const updateId = selectedCoin.id || selectedCoin.pcgs_id;

        axios.put(`${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001')}/coins/${updateId}`, updatedCoin)
            .then(() => {
                showNotification('Specimen deleted successfully!', 'success');
                // Refresh local state to reflect deletion without full reload or navigate
                setSelectedCoin(updatedCoin);
                initializeForm(updatedCoin);
                setEditMode('type'); // Go back to type mode or clear specimen selection
                setSelectedSpecimen(null);
            })
            .catch(err => {
                console.error('Error deleting specimen:', err);
                showNotification('Failed to delete specimen.', 'error');
            });
    };

    return (
        <Container maxWidth="md">
            <Paper sx={{ p: 4, mt: 4 }}>
                <Typography variant="h5" gutterBottom align="center">Edit Coin</Typography>

                {/* Step 1: SEARCH (Only if no coin selected or user wants to switch) */}
                <Box sx={{ mb: 4 }}>
                    <Autocomplete
                        options={coins}
                        getOptionLabel={(option) => `${option.date} ${option.denomination} ${option.series_date ? option.series_date + ' ' : ''}${option.series_name} (PCGS: ${option.pcgs_id})`}
                        value={selectedCoin}
                        onChange={handleCoinSelect}
                        renderInput={(params) => <TextField {...params} label="Search Coin to Edit" fullWidth />}
                    />
                </Box>

                {selectedCoin && (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                            <ToggleButtonGroup
                                color="primary"
                                value={editMode}
                                exclusive
                                onChange={handleModeChange}
                            >
                                <ToggleButton value="type">Edit Coin Details</ToggleButton>
                                <ToggleButton value="specimen">Edit Specimen</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        <form onSubmit={handleSubmit}>
                            {editMode === 'type' && (
                                <Stack spacing={2}>
                                    <Typography variant="h6">Coin Type Details</Typography>
                                    <TextField fullWidth label="PCGS ID" name="pcgs_id" value={coinData.pcgs_id} onChange={handleCoinChange} disabled />
                                    <Stack direction="row" spacing={2}>
                                        <TextField fullWidth label="Country" name="country" value={coinData.country} onChange={handleCoinChange} />
                                        <TextField fullWidth label="Denomination" name="denomination" value={coinData.denomination} onChange={handleCoinChange} />
                                    </Stack>
                                    <Stack direction="row" spacing={2}>
                                        <TextField fullWidth label="Date" name="date" value={coinData.date} onChange={handleCoinChange} />
                                        <TextField fullWidth label="Mint" name="mint" value={coinData.mint} onChange={handleCoinChange} />
                                    </Stack>
                                    <Stack direction="row" spacing={2}>
                                        <TextField fullWidth label="Series Name" name="series_name" value={coinData.series_name} onChange={handleCoinChange} />
                                        <TextField fullWidth label="Series Date" name="series_date" value={coinData.series_date} onChange={handleCoinChange} />
                                    </Stack>
                                    <TextField fullWidth label="Type" name="type" value={coinData.type} onChange={handleCoinChange} />
                                    <Stack direction="row" spacing={2}>
                                        <TextField fullWidth label="Major Variety" name="major" value={coinData.major} onChange={handleCoinChange} />
                                        <TextField fullWidth label="Minor Variety" name="minor" value={coinData.minor} onChange={handleCoinChange} />
                                    </Stack>
                                    <TextField fullWidth label="Status" name="status" value={coinData.status} onChange={handleCoinChange} />

                                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button variant="outlined" color="error" onClick={handleDeleteCoin}>
                                            Delete Coin Type
                                        </Button>
                                    </Box>
                                </Stack>
                            )}

                            {editMode === 'specimen' && (
                                <Stack spacing={2}>
                                    <Typography variant="h6">Specimen Details</Typography>

                                    {/* Specimen Selector */}
                                    <Autocomplete
                                        options={selectedCoin.specimens || []}
                                        getOptionLabel={(option) => `${option.grade} (${option.grader}) - Purchased: ${option.date_of_purchase}`}
                                        value={selectedSpecimen}
                                        onChange={handleSpecimenSelect}
                                        renderInput={(params) => <TextField {...params} label="Select Specimen to Edit" fullWidth />}
                                        disableClearable
                                    />

                                    {selectedSpecimen && (
                                        <>
                                            <Divider sx={{ my: 2 }} />
                                            <Stack direction="row" spacing={2}>
                                                <TextField fullWidth label="Grade" name="grade" value={specimenData.grade} onChange={handleSpecimenChange} />
                                                <TextField fullWidth label="Grader" name="grader" value={specimenData.grader} onChange={handleSpecimenChange} />
                                            </Stack>
                                            <Stack direction="row" spacing={2}>
                                                <TextField fullWidth label="Purchase Price" name="purchase_price" type="number" value={specimenData.purchase_price} onChange={handleSpecimenChange} />
                                                <TextField fullWidth label="PCGS Value" name="pcgs_value" type="number" value={specimenData.pcgs_value} onChange={handleSpecimenChange} />
                                            </Stack>
                                            <Stack direction="row" spacing={2}>
                                                <TextField fullWidth label="Date of Purchase" name="date_of_purchase" type="date" InputLabelProps={{ shrink: true }} value={specimenData.date_of_purchase} onChange={handleSpecimenChange} />
                                                <TextField fullWidth label="Storage Location" name="storage_location" value={specimenData.storage_location} onChange={handleSpecimenChange} />
                                            </Stack>
                                            <TextField fullWidth multiline rows={2} label="Comments" name="comments" value={specimenData.comments} onChange={handleSpecimenChange} />
                                            <FormControlLabel control={<Checkbox checked={specimenData.duplicate || false} onChange={handleSpecimenChange} name="duplicate" />} label="Duplicate" />

                                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                                <Button variant="outlined" color="error" onClick={handleDeleteSpecimen}>
                                                    Delete Specimen
                                                </Button>
                                            </Box>
                                        </>
                                    )}
                                    {!selectedSpecimen && <Typography color="text.secondary">Select a specimen above to edit.</Typography>}
                                </Stack>
                            )}

                            <Box sx={{ mt: 3 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    fullWidth
                                    disabled={editMode === 'specimen' && !selectedSpecimen}
                                >
                                    Save Changes
                                </Button>
                            </Box>
                        </form>
                    </>
                )}
            </Paper>
            <Snackbar open={notification.open} autoHideDuration={6000} onClose={handleCloseNotification}>
                <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}
